import { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, Check } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useLang } from '../i18n/LanguageContext';

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    guests: '',
    message: '',
  });
  const [interests, setInterests] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInterest = (s) => {
    setInterests((arr) => (arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t('contact.toasts.missing'));
      return;
    }
    setSending(true);
    try {
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;
      await axios.post(apiUrl, { ...form, interests }, { timeout: 30000 });
      setSubmitted(true);
      toast.success(t('contact.toasts.sent'));
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || '';
      toast.error(`${t('contact.toasts.failed')}${detail ? `: ${detail}` : ''}`);
    } finally {
      setSending(false);
    }
  };

  const eventTypes = t('contact.eventTypes');
  const interestStructures = t('contact.interests');
  const L = t('contact.labels');

  return (
    <main className="pt-32 lg:pt-40 pb-24 bg-cream">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="mb-12">
          <SectionLabel>{t('common.getInTouch')}</SectionLabel>
          <h1 className="font-serif-display text-6xl md:text-7xl lg:text-8xl leading-[0.95] text-dark">
            {t('contact.heading')}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <h2 className="font-serif-display text-3xl md:text-4xl text-dark mb-6">TL Event Group</h2>
            <p className="text-[15px] leading-relaxed text-muted-warm mb-10 font-light">
              {t('contact.intro')}
            </p>

            <div className="space-y-6">
              <a href="mailto:info@tleventgroup.com" className="flex items-start gap-4 group">
                <div className="shrink-0 w-11 h-11 border border-warm flex items-center justify-center group-hover:bg-dark group-hover:border-dark transition-colors">
                  <Mail size={16} className="text-gold group-hover:text-gold-soft transition-colors" strokeWidth={1.4} />
                </div>
                <div>
                  <p className="text-[10px] tracking-luxury uppercase text-muted-warm mb-1">{L.email}</p>
                  <p className="text-[15px] text-dark group-hover:text-gold transition-colors">info@tleventgroup.com</p>
                </div>
              </a>
              <a href="tel:+32472649659" className="flex items-start gap-4 group">
                <div className="shrink-0 w-11 h-11 border border-warm flex items-center justify-center group-hover:bg-dark group-hover:border-dark transition-colors">
                  <Phone size={16} className="text-gold group-hover:text-gold-soft transition-colors" strokeWidth={1.4} />
                </div>
                <div>
                  <p className="text-[10px] tracking-luxury uppercase text-muted-warm mb-1">{L.phone}</p>
                  <p className="text-[15px] text-dark group-hover:text-gold transition-colors">+32 472 64 96 59</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-11 h-11 border border-warm flex items-center justify-center">
                  <MapPin size={16} className="text-gold" strokeWidth={1.4} />
                </div>
                <div>
                  <p className="text-[10px] tracking-luxury uppercase text-muted-warm mb-1">{L.area}</p>
                  <p className="text-[15px] text-dark">{L.areaValue}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div className="border border-warm bg-cream-soft p-12 lg:p-16 text-center">
                <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-dark flex items-center justify-center">
                  <Check className="text-gold-soft" size={22} strokeWidth={1.6} />
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl text-dark mb-4">{t('contact.success.title')}</h3>
                <p className="text-[15px] text-muted-warm font-light max-w-md mx-auto">
                  {t('contact.success.text')}
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-8 text-[11px] tracking-luxury uppercase text-gold hover:text-dark transition-colors"
                >
                  {t('cta.newRequest')}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-7">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <Field label={L.fullName}>
                    <Input value={form.name} onChange={(e) => update('name', e.target.value)} className="input-luxe" required />
                  </Field>
                  <Field label={L.emailReq}>
                    <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="input-luxe" required />
                  </Field>
                  <Field label={L.phoneOpt}>
                    <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="input-luxe" />
                  </Field>
                  <Field label={L.eventType}>
                    <Select value={form.eventType} onValueChange={(v) => update('eventType', v)}>
                      <SelectTrigger className="h-12 rounded-none bg-transparent border-0 border-b border-warm focus:ring-0 focus:border-dark px-0 text-[15px]">
                        <SelectValue placeholder={L.selectType} />
                      </SelectTrigger>
                      <SelectContent className="bg-cream">
                        {eventTypes.map((tp) => (
                          <SelectItem key={tp} value={tp}>{tp}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={L.eventDate}>
                    <Input type="date" value={form.eventDate} onChange={(e) => update('eventDate', e.target.value)} className="input-luxe" />
                  </Field>
                  <Field label={L.guests}>
                    <Input type="number" min="0" value={form.guests} onChange={(e) => update('guests', e.target.value)} className="input-luxe" />
                  </Field>
                </div>

                <div>
                  <Label className="text-[10px] tracking-luxury uppercase text-muted-warm mb-4 block">{L.interests}</Label>
                  <div className="flex flex-wrap gap-2.5">
                    {interestStructures.map((s) => {
                      const active = interests.includes(s);
                      return (
                        <button
                          type="button"
                          key={s}
                          onClick={() => toggleInterest(s)}
                          className={`text-xs px-4 py-2.5 border transition-colors duration-300 ${
                            active ? 'bg-dark border-dark text-cream' : 'border-warm text-dark hover:border-dark'
                          }`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Field label={L.message}>
                  <Textarea
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    rows={5}
                    className="rounded-none bg-transparent border-0 border-b border-warm focus-visible:ring-0 focus:border-dark px-0 text-[15px] resize-none"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-3 text-[11px] tracking-luxury uppercase text-cream bg-dark px-9 py-4 hover:bg-[#2A241E] transition-colors duration-300 group disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? t('cta.submitting') : t('cta.submit')}
                  <Send size={14} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .input-luxe {
          height: 48px;
          background: transparent;
          border: 0;
          border-bottom: 1px solid #E0D8C9;
          border-radius: 0;
          padding: 0;
          font-size: 15px;
          color: #1F1B17;
        }
        .input-luxe:focus,
        .input-luxe:focus-visible {
          outline: none;
          border-bottom-color: #1F1B17;
          box-shadow: none;
        }
      `}</style>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-luxury uppercase text-muted-warm mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
